import { useState, useEffect, useCallback } from 'react';

interface UseFormAutoSaveOptions {
  key: string;
  initialData: any;
  enabled?: boolean;
}

export const useFormAutoSave = <T extends Record<string, any>>({
  key,
  initialData,
  enabled = true,
}: UseFormAutoSaveOptions) => {
  const [formData, setFormData] = useState<T>(initialData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load saved data on mount
  useEffect(() => {
    if (!enabled) return;

    try {
      const savedData = localStorage.getItem(`form-autosave-${key}`);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.warn('Failed to load auto-saved form data:', error);
    }
  }, [key, enabled]);

  // Save data to localStorage whenever formData changes
  useEffect(() => {
    if (!enabled) return;

    const timeoutId = setTimeout(() => {
      try {
        // Only save if there's actual content (not just empty strings)
        const hasContent = Object.values(formData).some((value) =>
          typeof value === 'string' ? value.trim() !== '' : Boolean(value),
        );

        if (hasContent) {
          localStorage.setItem(`form-autosave-${key}`, JSON.stringify(formData));
          setHasUnsavedChanges(true);
        }
      } catch (error) {
        console.warn('Failed to auto-save form data:', error);
      }
    }, 500); // Debounce saves by 500ms

    return () => clearTimeout(timeoutId);
  }, [formData, key, enabled]);

  // Clear saved data
  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem(`form-autosave-${key}`);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.warn('Failed to clear auto-saved form data:', error);
    }
  }, [key]);

  // Update form data
  const updateFormData = useCallback((updates: Partial<T>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Reset to initial data
  const resetFormData = useCallback(() => {
    setFormData(initialData);
    clearSavedData();
  }, [initialData, clearSavedData]);

  return {
    formData,
    setFormData,
    updateFormData,
    resetFormData,
    clearSavedData,
    hasUnsavedChanges,
  };
};
